import { orgAPI } from "@/api/api";
import type {
  ObjectActionDestinationSubmenu,
  ResolvedObjectAction,
  TransferDestination,
} from "@/object-actions/types";
import type { Folder } from "@cohesive/api-client";
import { useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useCallback, useMemo } from "react";
import toast from "react-hot-toast";
import { folderSubtreeIds } from "./canvasFolderTree";
import { useWritableWorkspaceIds } from "./useWritableWorkspaces";

/**
 * Move to for canvas/chat folders. One PATCH carries the whole intent: a new
 * parent within the workspace, or a new workspace — the server then relocates
 * the entire subtree (descendant folders and the resources filed in them) in
 * one transaction, so the client never orchestrates a cascade it could
 * half-finish.
 */
export function useCanvasFolderMoveCommit(
  folder: Folder | null,
): (destination: TransferDestination) => void {
  const queryClient = useQueryClient();
  const sourceWorkspaceId = folder?.workspace_id;

  return useCallback(
    (destination: TransferDestination) => {
      if (!folder?.id) return;
      void (async () => {
        try {
          await orgAPI.folders.update(folder.id, {
            parent_folder_id: destination.folderId ?? null,
            ...(destination.workspaceId !== sourceWorkspaceId
              ? { workspace_id: destination.workspaceId }
              : {}),
          });
          // The subtree moved server-side: refresh folders on both sides and
          // the resource lists, whose rows carry workspace_id/folder_id.
          void queryClient.invalidateQueries({
            queryKey: ["folders", sourceWorkspaceId],
          });
          void queryClient.invalidateQueries({
            queryKey: ["folders", destination.workspaceId],
          });
          void queryClient.invalidateQueries({ queryKey: ["canvases"] });
          void queryClient.invalidateQueries({ queryKey: ["canvas"] });
          void queryClient.invalidateQueries({
            queryKey: ["workspace-flow-sessions"],
          });
          toast.success(`Moved to ${destination.label}`);
        } catch (err) {
          if (err instanceof AxiosError && err.response?.status === 409) {
            toast.error("A folder with this name already exists there.");
            return;
          }
          toast.error("Failed to move folder.");
        }
      })();
    },
    [folder, queryClient, sourceWorkspaceId],
  );
}

/**
 * Decorate a folder's resolved actions with the canvas-folder destination
 * submenu. `allFolders` is the workspace's folder list — it feeds the
 * can't-move-into-own-subtree guard.
 */
export function useAttachCanvasFolderTransferSubmenu(
  actions: ResolvedObjectAction[],
  folder: Folder | null,
  allFolders: readonly Folder[],
): ResolvedObjectAction[] {
  const commitMove = useCanvasFolderMoveCommit(folder);
  const hasMove = actions.some((a) => a.id === "move");
  const writableWorkspaceIds = useWritableWorkspaceIds(hasMove);

  return useMemo(() => {
    if (!folder?.id || !hasMove) return actions;
    const submenu: ObjectActionDestinationSubmenu = {
      kind: "workspace-destination",
      operation: "move",
      commit: commitMove,
      treeSource: "canvas-folders",
      currentWorkspaceId: folder.workspace_id,
      // Current location = the folder's own parent ("" is the root), so the
      // one no-op row is disabled rather than the whole workspace.
      currentFolderPath: folder.parent_folder_id ?? "",
      forbiddenFolderIds: folderSubtreeIds([...allFolders], folder.id),
      writableWorkspaceIds,
    };
    return actions.map((action) =>
      action.id === "move" ? { ...action, submenu } : action,
    );
  }, [actions, allFolders, commitMove, folder, hasMove, writableWorkspaceIds]);
}
